<template>
  <div class="space-y-6">
    <!-- Basic Info Card -->
    <div
      :class="[
        theme.isDark.value ? 'bg-gray-800' : 'bg-white',
        'rounded-2xl overflow-hidden shadow-lg-custom'
      ]"
      style="box-shadow: var(--shadow-lg)"
    >
      <div class="p-6 space-y-6">
        <!-- Report Name -->
        <div>
          <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Report Name
          </label>
          <input
            v-model="form.name"
            type="text"
            class="w-full px-4 py-3 border rounded-xl text-sm focus:outline-none focus:ring-2 bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white focus:ring-emerald-500 focus:border-emerald-500 transition-all"
            placeholder="e.g., Daily Error Report"
          />
        </div>

        <!-- Status Selection -->
        <div class="p-5 bg-gray-50 dark:bg-gray-700/50 rounded-xl">
          <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
            Report Status
          </label>
          <div class="flex space-x-4">
            <button
              v-for="status in ['draft', 'active', 'inactive']"
              :key="status"
              type="button"
              :class="[
                form.status === status
                  ? status === 'active'
                    ? 'bg-emerald-500 text-white border-emerald-500'
                    : status === 'draft'
                      ? 'bg-amber-500 text-white border-amber-500'
                      : 'bg-gray-500 text-white border-gray-500'
                  : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600',
                'px-5 py-2.5 text-sm font-medium border rounded-xl transition-all duration-200 capitalize'
              ]"
              @click="form.status = status as ReportStatus"
            >
              {{ status }}
            </button>
          </div>
        </div>

        <!-- Schedule Section -->
        <div class="space-y-4">
          <div class="flex items-center">
            <div class="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg mr-3">
              <svg
                class="w-5 h-5 text-blue-500"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>
            <h4 class="text-base font-semibold text-gray-900 dark:text-white">Schedule</h4>
          </div>

          <div class="grid grid-cols-2 gap-5">
            <div>
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Schedule Type
              </label>
              <select
                v-model="form.schedule.type"
                class="w-full px-4 py-3 border rounded-xl text-sm focus:outline-none focus:ring-2 bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white focus:ring-emerald-500 focus:border-emerald-500 transition-all"
              >
                <option value="cron">Cron</option>
                <option value="manual">Manual</option>
              </select>
            </div>
            <div v-if="form.schedule.type === 'cron'">
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Cron Expression
              </label>
              <input
                v-model="form.schedule.expression"
                type="text"
                class="w-full px-4 py-3 border rounded-xl text-sm font-mono focus:outline-none focus:ring-2 bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white focus:ring-emerald-500 focus:border-emerald-500 transition-all"
                placeholder="e.g., 0 9 * * 1-5"
              />
            </div>
          </div>

          <div
            v-if="form.schedule.type === 'cron'"
            class="flex flex-wrap gap-2"
          >
            <button
              v-for="preset in cronPresets"
              :key="preset.value"
              type="button"
              :class="[
                form.schedule.expression === preset.value
                  ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 border-blue-300 dark:border-blue-700'
                  : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 border-gray-200 dark:border-gray-600',
                'px-4 py-1.5 text-xs font-medium border rounded-full transition-all duration-200 hover:bg-blue-50 dark:hover:bg-blue-900/20'
              ]"
              @click="form.schedule.expression = preset.value"
            >
              {{ preset.label }}
            </button>
          </div>

          <div
            v-if="form.schedule.type === 'cron' && form.schedule.expression"
            class="px-4 py-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-xl"
          >
            <div class="flex items-start">
              <svg
                class="w-5 h-5 text-blue-500 mt-0.5 mr-3 flex-shrink-0"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <div class="flex-1 space-y-2">
                <div>
                  <p class="text-xs font-medium text-blue-700 dark:text-blue-300 mb-0.5">
                    Human readable:
                  </p>
                  <p class="text-sm text-blue-600 dark:text-blue-400">
                    {{ parseCronExpression(form.schedule.expression) }}
                  </p>
                </div>
                <div v-if="getISTTime(form.schedule.expression)">
                  <p class="text-xs font-medium text-blue-700 dark:text-blue-300 mb-0.5">
                    IST Equivalent:
                  </p>
                  <p class="text-sm text-emerald-600 dark:text-emerald-400 font-medium">
                    {{ getISTTime(form.schedule.expression) }}
                  </p>
                </div>
                <div v-if="mode === 'edit' && props.reportData?.meta?.nextRun">
                  <p class="text-xs font-medium text-blue-700 dark:text-blue-300 mb-0.5">
                    Next Run:
                  </p>
                  <p class="text-sm text-violet-600 dark:text-violet-400 font-medium">
                    {{ formatEpochToIST(props.reportData.meta.nextRun) }}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Source Section with Preview -->
    <div class="flex gap-4">
      <!-- Source Form (Left) -->
      <div
        :class="[
          theme.isDark.value ? 'bg-gray-800' : 'bg-white',
          'rounded-2xl overflow-hidden shadow-lg-custom flex-1 transition-all duration-300',
          showSourcePreview ? 'max-w-[60%]' : 'max-w-full'
        ]"
        style="box-shadow: var(--shadow-lg)"
      >
        <!-- Section Header -->
        <button
          type="button"
          class="w-full px-6 py-4 flex items-center justify-between border-b transition-colors"
          :class="[
            theme.isDark.value
              ? 'border-gray-700 hover:bg-gray-700/50'
              : 'border-gray-100 hover:bg-gray-50'
          ]"
          @click="sections.source = !sections.source"
        >
          <div class="flex items-center">
            <div class="p-2.5 bg-blue-100 dark:bg-blue-900/30 rounded-xl mr-4">
              <svg
                class="w-6 h-6 text-blue-500"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9"
                />
              </svg>
            </div>
            <div class="text-left">
              <h3 class="text-lg font-semibold text-gray-900 dark:text-white">Source</h3>
              <p class="text-sm text-gray-500 dark:text-gray-400">
                HTTP API endpoint configuration
              </p>
            </div>
          </div>
          <div class="flex items-center space-x-3">
            <span
              v-if="form.source.config.url"
              class="text-xs font-mono text-gray-500 dark:text-gray-400 max-w-xs truncate"
            >
              {{ form.source.config.method }} {{ form.source.config.url }}
            </span>
            <!-- Preview Button -->
            <button
              v-if="
                FEATURE_TOGGLES.ENABLE_SOURCE_PREVIEW && mode === 'edit' && props.reportData?.id
              "
              type="button"
              class="flex items-center space-x-1.5 px-3 py-1.5 text-xs font-medium text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20 hover:bg-blue-100 dark:hover:bg-blue-900/40 border border-blue-200 dark:border-blue-700 rounded-lg transition-all duration-200"
              :disabled="sourcePreviewLoading"
              @click.stop="previewSourceStage"
            >
              <svg
                v-if="sourcePreviewLoading"
                class="animate-spin h-3.5 w-3.5"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  class="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  stroke-width="4"
                ></circle>
                <path
                  class="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                ></path>
              </svg>
              <svg
                v-else
                class="h-3.5 w-3.5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                />
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                />
              </svg>
              <span>Preview</span>
            </button>
            <svg
              :class="[
                'w-5 h-5 text-gray-400 transition-transform duration-200',
                sections.source ? 'rotate-180' : ''
              ]"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M19 9l-7 7-7-7"
              />
            </svg>
          </div>
        </button>

        <!-- Section Content -->
        <transition
          enter-active-class="transition-all duration-300 ease-out"
          enter-from-class="opacity-0 max-h-0"
          enter-to-class="opacity-100 max-h-[2000px]"
          leave-active-class="transition-all duration-200 ease-in"
          leave-from-class="opacity-100 max-h-[2000px]"
          leave-to-class="opacity-0 max-h-0"
        >
          <div
            v-show="sections.source"
            class="overflow-hidden"
          >
            <div class="p-6 space-y-5">
              <!-- Import/Export cURL Buttons -->
              <div
                v-if="FEATURE_TOGGLES.ENABLE_CURL_IMPORT || FEATURE_TOGGLES.ENABLE_CURL_EXPORT"
                class="flex justify-end space-x-2"
              >
                <button
                  v-if="FEATURE_TOGGLES.ENABLE_CURL_EXPORT && !showCurlInput"
                  type="button"
                  class="flex items-center space-x-2 px-4 py-2 text-sm font-medium text-violet-600 dark:text-violet-400 bg-violet-50 dark:bg-violet-900/20 hover:bg-violet-100 dark:hover:bg-violet-900/40 border border-violet-200 dark:border-violet-700 rounded-xl transition-all duration-200"
                  @click="exportAsCurl"
                >
                  <svg
                    class="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
                    />
                  </svg>
                  <span>Export as cURL</span>
                </button>
                <button
                  v-if="FEATURE_TOGGLES.ENABLE_CURL_IMPORT && !showCurlInput"
                  type="button"
                  class="flex items-center space-x-2 px-4 py-2 text-sm font-medium text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20 hover:bg-blue-100 dark:hover:bg-blue-900/40 border border-blue-200 dark:border-blue-700 rounded-xl transition-all duration-200"
                  @click="showCurlInput = true"
                >
                  <svg
                    class="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12"
                    />
                  </svg>
                  <span>Import cURL</span>
                </button>
              </div>

              <!-- cURL Import Input -->
              <div
                v-if="showCurlInput"
                class="p-5 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-xl space-y-4"
              >
                <div class="flex items-center justify-between">
                  <label class="text-sm font-medium text-blue-700 dark:text-blue-300">
                    Paste cURL command
                  </label>
                  <button
                    type="button"
                    class="p-1.5 text-blue-500 hover:text-blue-700 dark:hover:text-blue-300 hover:bg-blue-100 dark:hover:bg-blue-800/30 rounded-lg transition-colors"
                    @click="() => {
                      showCurlInput = false
                      curlInput = ''
                      curlError = null
                    }"
                  >
                    <svg
                      class="w-4 h-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  </button>
                </div>
                <textarea
                  ref="curlInputRef"
                  v-model="curlInput"
                  rows="4"
                  class="w-full px-4 py-3 border rounded-xl text-sm font-mono focus:outline-none focus:ring-2 bg-white dark:bg-gray-700 border-blue-300 dark:border-blue-600 text-gray-900 dark:text-white focus:ring-blue-500 focus:border-blue-500"
                  placeholder="curl -X POST 'https://api.example.com/v1/data' -H 'Authorization: Bearer token' -H 'Content-Type: application/json' -d '{&quot;key&quot;: &quot;value&quot;}'"
                  @keydown.enter.prevent="parseCurlCommand"
                ></textarea>
                <p
                  v-if="curlError"
                  class="text-xs text-rose-500"
                >
                  {{ curlError }}
                </p>
                <div class="flex items-center justify-between">
                  <p class="text-xs text-blue-600 dark:text-blue-400">Press Enter to import</p>
                  <button
                    type="button"
                    class="px-4 py-2 text-sm font-medium text-white bg-blue-500 hover:bg-blue-600 rounded-xl transition-colors"
                    @click="parseCurlCommand"
                  >
                    Import
                  </button>
                </div>
              </div>

              <div class="grid grid-cols-2 gap-5">
                <div>
                  <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    HTTP Method <span class="text-rose-500">*</span>
                  </label>
                  <select
                    v-model="form.source.config.method"
                    class="w-full px-4 py-3 border rounded-xl text-sm focus:outline-none focus:ring-2 bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white focus:ring-emerald-500 focus:border-emerald-500 transition-all"
                  >
                    <option value="GET">GET</option>
                    <option value="POST">POST</option>
                    <option value="PUT">PUT</option>
                    <option value="PATCH">PATCH</option>
                    <option value="DELETE">DELETE</option>
                  </select>
                </div>
                <div>
                  <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Timeout (ms)
                  </label>
                  <input
                    v-model.number="form.source.config.timeout"
                    type="number"
                    min="1000"
                    max="300000"
                    step="1000"
                    class="w-full px-4 py-3 border rounded-xl text-sm focus:outline-none focus:ring-2 bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white focus:ring-emerald-500 focus:border-emerald-500 transition-all"
                    placeholder="30000"
                  />
                </div>
              </div>

              <div>
                <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  URL <span class="text-rose-500">*</span>
                </label>
                <input
                  v-model="form.source.config.url"
                  type="text"
                  required
                  class="w-full px-4 py-3 border rounded-xl text-sm font-mono focus:outline-none focus:ring-2 bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white focus:ring-emerald-500 focus:border-emerald-500 transition-all"
                  placeholder="https://api.example.com/v1/data"
                />
              </div>

              <div>
                <div class="flex items-center justify-between mb-2">
                  <label class="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Headers
                  </label>
                  <button
                    type="button"
                    class="flex items-center space-x-1 text-sm text-emerald-600 dark:text-emerald-400 hover:text-emerald-700 font-medium transition-colors"
                    @click="addHeader"
                  >
                    <svg
                      class="w-4 h-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M12 6v6m0 0v6m0-6h6m-6 0H6"
                      />
                    </svg>
                    <span>Add Header</span>
                  </button>
                </div>
                <div class="space-y-3">
                  <div
                    v-for="(_, index) in headersList"
                    :key="index"
                    class="flex items-center gap-3"
                  >
                    <input
                      v-model="headersList[index].key"
                      type="text"
                      class="flex-1 px-4 py-2.5 border rounded-xl text-sm font-mono focus:outline-none focus:ring-2 bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white focus:ring-emerald-500 focus:border-emerald-500 transition-all"
                      placeholder="Header name"
                    />
                    <input
                      v-model="headersList[index].value"
                      type="text"
                      class="flex-1 px-4 py-2.5 border rounded-xl text-sm font-mono focus:outline-none focus:ring-2 bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white focus:ring-emerald-500 focus:border-emerald-500 transition-all"
                      placeholder="Header value"
                    />
                    <button
                      type="button"
                      class="p-2 text-rose-500 hover:bg-rose-50 dark:hover:bg-rose-900/30 rounded-xl transition-colors"
                      @click="removeHeader(index)"
                    >
                      <svg
                        class="w-5 h-5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          stroke-width="2"
                          d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                        />
                      </svg>
                    </button>
                  </div>
                  <p
                    v-if="headersList.length === 0"
                    class="text-sm text-gray-500 dark:text-gray-400 italic py-2"
                  >
                    No headers added yet
                  </p>
                </div>
              </div>

              <div v-if="['POST', 'PUT', 'PATCH'].includes(form.source.config.method)">
                <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Request Body (JSON)
                </label>
                <textarea
                  v-model="bodyJson"
                  rows="5"
                  class="w-full px-4 py-3 border rounded-xl text-sm font-mono focus:outline-none focus:ring-2 bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white focus:ring-emerald-500 focus:border-emerald-500 transition-all"
                  placeholder='{"key": "value"}'
                ></textarea>
                <p
                  v-if="bodyJsonError"
                  class="mt-2 text-xs text-rose-500"
                >
                  {{ bodyJsonError }}
                </p>
              </div>
            </div>
          </div>
        </transition>
      </div>

      <!-- Source Preview Panel (Right) -->
      <transition
        enter-active-class="transition-all duration-300 ease-out"
        enter-from-class="opacity-0 translate-x-4 w-0"
        enter-to-class="opacity-100 translate-x-0 w-[40%]"
        leave-active-class="transition-all duration-200 ease-in"
        leave-from-class="opacity-100 translate-x-0 w-[40%]"
        leave-to-class="opacity-0 translate-x-4 w-0"
      >
        <div
          v-if="showSourcePreview || sourcePreviewError"
          :class="[
            theme.isDark.value ? 'bg-gray-800' : 'bg-white',
            'rounded-2xl overflow-hidden shadow-lg-custom w-[40%] flex-shrink-0'
          ]"
          style="box-shadow: var(--shadow-lg)"
        >
          <div
            class="px-4 py-3 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between"
          >
            <h4 class="text-sm font-semibold text-gray-900 dark:text-white flex items-center">
              <svg
                class="w-4 h-4 mr-2 text-blue-500"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                />
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                />
              </svg>
              Source Preview
            </h4>
            <button
              type="button"
              class="p-1.5 text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              @click="() => {
                showSourcePreview = false
                sourcePreviewData = null
                sourcePreviewError = null
              }"
            >
              <svg
                class="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
          <div class="p-4 max-h-[600px] overflow-y-auto">
            <!-- Error State -->
            <div
              v-if="sourcePreviewError"
              class="p-4 bg-rose-50 dark:bg-rose-900/20 border border-rose-200 dark:border-rose-700 rounded-xl"
            >
              <div class="flex items-start">
                <svg
                  class="w-5 h-5 text-rose-500 mt-0.5 mr-3 flex-shrink-0"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                <div class="flex-1">
                  <p class="text-sm font-medium text-rose-700 dark:text-rose-300">Preview Error</p>
                  <p class="text-sm text-rose-600 dark:text-rose-400 mt-1">
                    {{ sourcePreviewError }}
                  </p>
                </div>
              </div>
            </div>
            <!-- Preview Content -->
            <div
              v-else-if="sourcePreviewData"
              class="space-y-3"
            >
              <div class="flex items-center justify-between">
                <span class="text-xs font-medium text-gray-500 dark:text-gray-400">
                  Stage:
                  <span class="text-blue-600 dark:text-blue-400">{{
                    sourcePreviewData.stage
                  }}</span>
                </span>
                <span
                  :class="[
                    sourcePreviewData.success
                      ? 'text-emerald-600 dark:text-emerald-400'
                      : 'text-rose-600 dark:text-rose-400',
                    'text-xs font-medium'
                  ]"
                >
                  {{ sourcePreviewData.success ? 'Success' : 'Failed' }}
                </span>
              </div>
              <pre
                class="text-xs font-mono text-gray-800 dark:text-gray-200 overflow-x-auto whitespace-pre-wrap p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg border border-gray-200 dark:border-gray-600"
                >{{ JSON.stringify(sourcePreviewData.data, null, 2) }}</pre
              >
            </div>
          </div>
        </div>
      </transition>
    </div>

    <!-- Template Section with Preview -->
    <div class="flex gap-4">
      <!-- Template Form (Left) -->
      <div
        :class="[
          theme.isDark.value ? 'bg-gray-800' : 'bg-white',
          'rounded-2xl overflow-hidden shadow-lg-custom transition-all duration-300',
          showTransformPreview ? 'w-[550px] flex-shrink-0' : 'flex-1'
        ]"
        style="box-shadow: var(--shadow-lg)"
      >
        <!-- Section Header -->
        <button
          type="button"
          class="w-full px-6 py-4 flex items-center justify-between border-b transition-colors"
          :class="[
            theme.isDark.value
              ? 'border-gray-700 hover:bg-gray-700/50'
              : 'border-gray-100 hover:bg-gray-50'
          ]"
          @click="sections.template = !sections.template"
        >
          <div class="flex items-center">
            <div class="p-2.5 bg-violet-100 dark:bg-violet-900/30 rounded-xl mr-4">
              <svg
                class="w-6 h-6 text-violet-500"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M8 9l3 3-3 3m5 0h3M5 20h14a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                />
              </svg>
            </div>
            <div class="text-left">
              <h3 class="text-lg font-semibold text-gray-900 dark:text-white">Template</h3>
              <p class="text-sm text-gray-500 dark:text-gray-400">
                Handlebars message template configuration
              </p>
            </div>
          </div>
          <div class="flex items-center space-x-3">
            <span
              v-if="fileConfigEnabled"
              class="px-2 py-1 text-xs font-medium bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 rounded-lg"
            >
              PDF/File Enabled
            </span>
            <!-- Preview Button -->
            <button
              v-if="
                FEATURE_TOGGLES.ENABLE_TRANSFORM_PREVIEW && mode === 'edit' && props.reportData?.id
              "
              type="button"
              class="flex items-center space-x-1.5 px-3 py-1.5 text-xs font-medium text-violet-600 dark:text-violet-400 bg-violet-50 dark:bg-violet-900/20 hover:bg-violet-100 dark:hover:bg-violet-900/40 border border-violet-200 dark:border-violet-700 rounded-lg transition-all duration-200"
              :disabled="transformPreviewLoading"
              @click.stop="previewTransformStage"
            >
              <svg
                v-if="transformPreviewLoading"
                class="animate-spin h-3.5 w-3.5"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  class="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  stroke-width="4"
                ></circle>
                <path
                  class="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                ></path>
              </svg>
              <svg
                v-else
                class="h-3.5 w-3.5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                />
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                />
              </svg>
              <span>Preview</span>
            </button>
            <svg
              :class="[
                'w-5 h-5 text-gray-400 transition-transform duration-200',
                sections.template ? 'rotate-180' : ''
              ]"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M19 9l-7 7-7-7"
              />
            </svg>
          </div>
        </button>

        <!-- Section Content -->
        <transition
          enter-active-class="transition-all duration-300 ease-out"
          enter-from-class="opacity-0 max-h-0"
          enter-to-class="opacity-100 max-h-[2000px]"
          leave-active-class="transition-all duration-200 ease-in"
          leave-from-class="opacity-100 max-h-[2000px]"
          leave-to-class="opacity-0 max-h-0"
        >
          <div
            v-show="sections.template"
            class="overflow-hidden"
          >
            <div class="p-6 space-y-5">
              <div>
                <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Message Template <span class="text-rose-500">*</span>
                </label>
                <textarea
                  v-model="form.transform.config.template"
                  required
                  rows="8"
                  class="w-full px-4 py-3 border rounded-xl text-sm font-mono focus:outline-none focus:ring-2 bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white focus:ring-emerald-500 focus:border-emerald-500 transition-all"
                  placeholder="*Daily Report* :chart_with_upwards_trend:

{{#each items}}
• *{{name}}*: {{value}}
{{/each}}

_Generated at {{timestamp}}_"
                ></textarea>
                <p class="mt-2 text-xs text-gray-500 dark:text-gray-400">
                  Use Handlebars syntax to reference data from your API response. All variables
                  automatically point to
                  <code class="px-1.5 py-0.5 bg-gray-100 dark:bg-gray-700 rounded-md"
                    >data.value</code
                  >
                  from the response, so you can access nested properties directly. For example, if
                  your API returns
                  <code class="px-1.5 py-0.5 bg-gray-100 dark:bg-gray-700 rounded-md"
                    >data.value.message</code
                  >, simply use
                  <code class="px-1.5 py-0.5 bg-gray-100 dark:bg-gray-700 rounded-md"
                    >&#123;&#123;message&#125;&#125;</code
                  >
                  in your template.
                </p>
              </div>

              <div
                class="flex items-center justify-between p-5 bg-gray-50 dark:bg-gray-700/50 rounded-xl"
              >
                <div>
                  <span class="text-sm font-medium text-gray-700 dark:text-gray-300"
                    >Enable PDF/File Generation</span
                  >
                  <p class="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    Generate and attach a PDF or CSV file
                  </p>
                </div>
                <button
                  type="button"
                  :class="[
                    fileConfigEnabled ? 'bg-emerald-500' : 'bg-gray-300 dark:bg-gray-600',
                    'relative inline-flex h-7 w-12 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2'
                  ]"
                  @click="fileConfigEnabled = !fileConfigEnabled"
                >
                  <span
                    :class="[
                      fileConfigEnabled ? 'translate-x-5' : 'translate-x-0',
                      'pointer-events-none inline-block h-6 w-6 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out'
                    ]"
                  ></span>
                </button>
              </div>

              <div
                v-if="fileConfigEnabled"
                class="p-5 bg-gray-50 dark:bg-gray-700/50 rounded-xl space-y-5 border border-gray-200 dark:border-gray-600"
              >
                <h5 class="text-sm font-medium text-gray-700 dark:text-gray-300 flex items-center">
                  <span class="text-lg mr-2">📎</span> File Generation Configuration
                  <span class="ml-2 text-xs font-normal text-gray-500 dark:text-gray-400"
                    >(Optional - defaults will be used if not specified)</span
                  >
                </h5>

                <div class="grid grid-cols-2 gap-5">
                  <div>
                    <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Data Path
                    </label>
                    <input
                      v-model="form.transform.config.fileConfig!.dataPath"
                      type="text"
                      class="w-full px-4 py-3 border rounded-xl text-sm font-mono focus:outline-none focus:ring-2 bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white focus:ring-emerald-500 focus:border-emerald-500 transition-all"
                      placeholder="data.value"
                    />
                    <p class="mt-1 text-xs text-gray-500 dark:text-gray-400">
                      JSONPath to extract array from the API response. Points to
                      <code class="px-1 py-0.5 bg-gray-200 dark:bg-gray-600 rounded"
                        >data.value</code
                      >
                      by default. If your response contains an array at
                      <code class="px-1 py-0.5 bg-gray-200 dark:bg-gray-600 rounded"
                        >data.value.results</code
                      >, you can reference it in your template using
                      <code class="px-1 py-0.5 bg-gray-200 dark:bg-gray-600 rounded"
                        >&#123;&#123;results&#125;&#125;</code
                      >.
                    </p>
                  </div>
                  <div>
                    <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Top N Rows
                    </label>
                    <input
                      v-model.number="form.transform.config.fileConfig!.topN"
                      type="number"
                      min="1"
                      max="1000"
                      class="w-full px-4 py-3 border rounded-xl text-sm focus:outline-none focus:ring-2 bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white focus:ring-emerald-500 focus:border-emerald-500 transition-all"
                      placeholder="20"
                    />
                    <p class="mt-1 text-xs text-gray-500 dark:text-gray-400">
                      Default:
                      <code class="px-1 py-0.5 bg-gray-200 dark:bg-gray-600 rounded">20</code>
                    </p>
                  </div>
                </div>

                <div>
                  <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Column Headers
                  </label>
                  <input
                    v-model="fileConfigHeaders"
                    type="text"
                    class="w-full px-4 py-3 border rounded-xl text-sm font-mono focus:outline-none focus:ring-2 bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white focus:ring-emerald-500 focus:border-emerald-500 transition-all"
                    placeholder="Auto-detected from first result object"
                  />
                  <p class="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    Comma-separated list. Leave empty to auto-detect from first JSON object keys
                  </p>
                </div>

                <!-- Sort Configuration (Optional) -->
                <div class="grid grid-cols-2 gap-5">
                  <div>
                    <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Sort By Columns
                    </label>
                    <input
                      v-model="sortColumns"
                      type="text"
                      class="w-full px-4 py-3 border rounded-xl text-sm font-mono focus:outline-none focus:ring-2 bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white focus:ring-emerald-500 focus:border-emerald-500 transition-all"
                      placeholder="e.g., count, name"
                    />
                    <p class="mt-1 text-xs text-gray-500 dark:text-gray-400">
                      Comma-separated column names (in priority order). Leave empty for no sorting
                    </p>
                  </div>
                  <div>
                    <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Sort Order
                    </label>
                    <select
                      v-model="sortOrder"
                      class="w-full px-4 py-3 border rounded-xl text-sm focus:outline-none focus:ring-2 bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white focus:ring-emerald-500 focus:border-emerald-500 transition-all"
                    >
                      <option value="desc">Descending (High to Low)</option>
                      <option value="asc">Ascending (Low to High)</option>
                    </select>
                    <p class="mt-1 text-xs text-gray-500 dark:text-gray-400">
                      Default:
                      <code class="px-1 py-0.5 bg-gray-200 dark:bg-gray-600 rounded">desc</code>
                    </p>
                  </div>
                </div>

                <!-- File Attachment Settings -->
                <div class="pt-4 border-t border-gray-200 dark:border-gray-600">
                  <h6
                    class="text-sm font-medium text-gray-700 dark:text-gray-300 mb-4 flex items-center"
                  >
                    <span class="text-lg mr-2">📎</span> File Attachment Settings
                  </h6>

                  <div class="grid grid-cols-2 gap-5">
                    <div>
                      <label
                        class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                      >
                        Filename <span class="text-rose-500">*</span>
                      </label>
                      <input
                        v-model="form.sink.config.file!.filename"
                        type="text"
                        class="w-full px-4 py-3 border rounded-xl text-sm focus:outline-none focus:ring-2 bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white focus:ring-emerald-500 focus:border-emerald-500 transition-all"
                        placeholder="report.pdf"
                      />
                    </div>
                    <div>
                      <label
                        class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                      >
                        File Title
                      </label>
                      <input
                        v-model="form.sink.config.file!.title"
                        type="text"
                        class="w-full px-4 py-3 border rounded-xl text-sm focus:outline-none focus:ring-2 bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white focus:ring-emerald-500 focus:border-emerald-500 transition-all"
                        placeholder="Weekly Report"
                      />
                    </div>
                  </div>

                  <div class="grid grid-cols-2 gap-5 mt-5">
                    <div>
                      <label
                        class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                      >
                        Before Content
                      </label>
                      <input
                        v-model="form.sink.config.file!.beforeContent"
                        type="text"
                        class="w-full px-4 py-3 border rounded-xl text-sm focus:outline-none focus:ring-2 bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white focus:ring-emerald-500 focus:border-emerald-500 transition-all"
                        placeholder="Text before the table"
                      />
                    </div>
                    <div>
                      <label
                        class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                      >
                        After Content
                      </label>
                      <input
                        v-model="form.sink.config.file!.afterContent"
                        type="text"
                        class="w-full px-4 py-3 border rounded-xl text-sm focus:outline-none focus:ring-2 bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white focus:ring-emerald-500 focus:border-emerald-500 transition-all"
                        placeholder="Text after the table"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </transition>
      </div>

      <!-- Transform Preview Panel (Right) -->
      <transition
        enter-active-class="transition-all duration-300 ease-out"
        enter-from-class="opacity-0 translate-x-4 w-0"
        enter-to-class="opacity-100 translate-x-0 flex-1"
        leave-active-class="transition-all duration-200 ease-in"
        leave-from-class="opacity-100 translate-x-0 flex-1"
        leave-to-class="opacity-0 translate-x-4 w-0"
      >
        <div
          v-if="showTransformPreview || transformPreviewError"
          :class="[
            theme.isDark.value ? 'bg-gray-800' : 'bg-white',
            'rounded-2xl overflow-hidden shadow-lg-custom flex-1 min-w-[400px]'
          ]"
          style="box-shadow: var(--shadow-lg)"
        >
          <div
            class="px-4 py-3 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between"
          >
            <h4 class="text-sm font-semibold text-gray-900 dark:text-white flex items-center">
              <svg
                class="w-4 h-4 mr-2 text-violet-500"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                />
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                />
              </svg>
              Transform Preview
            </h4>
            <button
              type="button"
              class="p-1.5 text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              @click="() => {
                showTransformPreview = false
                transformPreviewData = null
                transformPreviewError = null
              }"
            >
              <svg
                class="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
          <div class="max-h-[600px] overflow-y-auto">
            <!-- Error State -->
            <div
              v-if="transformPreviewError"
              class="p-4"
            >
              <div
                class="p-4 bg-rose-50 dark:bg-rose-900/20 border border-rose-200 dark:border-rose-700 rounded-xl"
              >
                <div class="flex items-start">
                  <svg
                    class="w-5 h-5 text-rose-500 mt-0.5 mr-3 flex-shrink-0"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                  <div class="flex-1">
                    <p class="text-sm font-medium text-rose-700 dark:text-rose-300">
                      Preview Error
                    </p>
                    <p class="text-sm text-rose-600 dark:text-rose-400 mt-1">
                      {{ transformPreviewError }}
                    </p>
                  </div>
                </div>
              </div>
            </div>
            <!-- Slack-style Message Preview -->
            <div v-else-if="transformPreviewData">
              <div class="rounded-b-xl overflow-hidden">
                <!-- Slack Header -->
                <div class="px-4 py-3 bg-[#1a1d21] flex items-center space-x-3">
                  <div class="w-9 h-9 rounded-lg bg-[#4A154B] flex items-center justify-center">
                    <svg
                      class="w-5 h-5 text-white"
                      viewBox="0 0 24 24"
                      fill="currentColor"
                    >
                      <path
                        d="M5.042 15.165a2.528 2.528 0 0 1-2.52 2.523A2.528 2.528 0 0 1 0 15.165a2.527 2.527 0 0 1 2.522-2.52h2.52v2.52zM6.313 15.165a2.527 2.527 0 0 1 2.521-2.52 2.527 2.527 0 0 1 2.521 2.52v6.313A2.528 2.528 0 0 1 8.834 24a2.528 2.528 0 0 1-2.521-2.522v-6.313zM8.834 5.042a2.528 2.528 0 0 1-2.521-2.52A2.528 2.528 0 0 1 8.834 0a2.528 2.528 0 0 1 2.521 2.522v2.52H8.834zM8.834 6.313a2.528 2.528 0 0 1 2.521 2.521 2.528 2.528 0 0 1-2.521 2.521H2.522A2.528 2.528 0 0 1 0 8.834a2.528 2.528 0 0 1 2.522-2.521h6.312zM18.956 8.834a2.528 2.528 0 0 1 2.522-2.521A2.528 2.528 0 0 1 24 8.834a2.528 2.528 0 0 1-2.522 2.521h-2.522V8.834zM17.688 8.834a2.528 2.528 0 0 1-2.523 2.521 2.527 2.527 0 0 1-2.52-2.521V2.522A2.527 2.527 0 0 1 15.165 0a2.528 2.528 0 0 1 2.523 2.522v6.312zM15.165 18.956a2.528 2.528 0 0 1 2.523 2.522A2.528 2.528 0 0 1 15.165 24a2.527 2.527 0 0 1-2.52-2.522v-2.522h2.52zM15.165 17.688a2.527 2.527 0 0 1-2.52-2.523 2.526 2.526 0 0 1 2.52-2.52h6.313A2.527 2.527 0 0 1 24 15.165a2.528 2.528 0 0 1-2.522 2.523h-6.313z"
                      />
                    </svg>
                  </div>
                  <div>
                    <div class="flex items-center space-x-2">
                      <span class="text-white font-bold text-sm">Slack Preview</span>
                      <span
                        class="px-1.5 py-0.5 text-[10px] font-medium bg-gray-600 text-gray-300 rounded"
                        >APP</span
                      >
                    </div>
                    <span class="text-gray-400 text-xs">{{
                      new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
                    }}</span>
                  </div>
                </div>

                <!-- Message Content -->
                <div class="px-4 py-4 bg-[#1a1d21]">
                  <div
                    class="text-[15px] text-gray-100 leading-relaxed slack-message-preview"
                    v-html="formatSlackMessage(String(transformPreviewData.data?.message || ''))"
                  ></div>
                </div>

                <!-- PDF Preview (if file data exists) -->
                <div
                  v-if="getFileData(transformPreviewData.data)"
                  class="border-t border-gray-700"
                >
                  <!-- PDF Preview Header -->
                  <div class="px-4 py-2 bg-[#222529] flex items-center justify-between">
                    <div class="flex items-center space-x-2">
                      <svg
                        class="w-4 h-4 text-red-500"
                        fill="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-6zM6 20V4h7v5h5v11H6z"
                        />
                        <path d="M8 12h8v1H8zm0 2h8v1H8zm0 2h5v1H8z" />
                      </svg>
                      <span class="text-xs font-medium text-gray-300">PDF Preview</span>
                    </div>
                    <span class="text-[10px] text-gray-500"
                      >{{ getFileDataRowCount(transformPreviewData.data) }} rows total</span
                    >
                  </div>

                  <!-- PDF Content -->
                  <div class="bg-white p-6 max-h-[400px] overflow-y-auto">
                    <!-- Title -->
                    <h2
                      v-if="getSinkFileConfig().title"
                      class="text-xl font-bold text-gray-900 mb-2"
                    >
                      {{ getSinkFileConfig().title }}
                    </h2>

                    <!-- Generated timestamp -->
                    <p class="text-xs text-gray-500 italic mb-4">
                      Generated: {{ new Date().toISOString() }}
                    </p>

                    <!-- Before Content text -->
                    <p
                      v-if="getSinkFileConfig().beforeContent"
                      class="text-sm text-gray-700 mb-4"
                    >
                      {{ getSinkFileConfig().beforeContent }}
                    </p>

                    <!-- Data Table -->
                    <div class="w-full">
                      <table class="w-full text-sm border-collapse">
                        <thead>
                          <tr class="bg-[#4472C4]">
                            <th
                              v-for="header in getFileDataHeaders(transformPreviewData.data)"
                              :key="header"
                              class="px-4 py-2.5 text-left text-xs font-semibold text-white capitalize border border-[#4472C4]"
                            >
                              {{ header }}
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr
                            v-for="(row, index) in getFileDataRows(
                              transformPreviewData.data,
                              showAllPdfRows ? 20 : 5
                            )"
                            :key="index"
                            :class="index % 2 === 0 ? 'bg-white' : 'bg-gray-50'"
                          >
                            <td
                              v-for="header in getFileDataHeaders(transformPreviewData.data)"
                              :key="header"
                              class="px-4 py-2 text-gray-900 border border-gray-200 break-words"
                            >
                              {{
                                typeof row[header] === 'number'
                                  ? row[header].toLocaleString()
                                  : row[header]
                              }}
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>

                    <!-- Show more/less button -->
                    <div
                      v-if="getFileDataRowCount(transformPreviewData.data) > 5"
                      class="mt-3 text-center"
                    >
                      <button
                        type="button"
                        class="text-xs text-blue-600 hover:text-blue-800 font-medium"
                        @click="showAllPdfRows = !showAllPdfRows"
                      >
                        {{
                          showAllPdfRows
                            ? 'Show less'
                            : `Show more (up to 20 of ${getFileDataRowCount(transformPreviewData.data)})`
                        }}
                      </button>
                    </div>

                    <!-- After Content text -->
                    <p
                      v-if="getSinkFileConfig().afterContent"
                      class="text-xs text-gray-500 mt-6 pt-4 border-t border-gray-200"
                    >
                      {{ getSinkFileConfig().afterContent }}
                    </p>
                  </div>
                </div>
              </div>

              <!-- Status footer -->
              <div
                class="px-4 py-3 flex items-center justify-between text-xs text-gray-500 dark:text-gray-400 border-t border-gray-200 dark:border-gray-700"
              >
                <span
                  >Stage:
                  <span class="text-violet-600 dark:text-violet-400">{{
                    transformPreviewData.stage
                  }}</span></span
                >
                <span
                  :class="
                    transformPreviewData.success
                      ? 'text-emerald-600 dark:text-emerald-400'
                      : 'text-rose-600 dark:text-rose-400'
                  "
                >
                  {{ transformPreviewData.success ? 'Success' : 'Failed' }}
                </span>
              </div>
            </div>
          </div>
        </div>
      </transition>
    </div>

    <!-- Sink Section (Collapsible) -->
    <div
      :class="[
        theme.isDark.value ? 'bg-gray-800' : 'bg-white',
        'rounded-2xl overflow-hidden shadow-lg-custom'
      ]"
      style="box-shadow: var(--shadow-lg)"
    >
      <!-- Section Header -->
      <button
        type="button"
        class="w-full px-6 py-4 flex items-center justify-between border-b transition-colors"
        :class="[
          theme.isDark.value
            ? 'border-gray-700 hover:bg-gray-700/50'
            : 'border-gray-100 hover:bg-gray-50'
        ]"
        @click="sections.sink = !sections.sink"
      >
        <div class="flex items-center">
          <div class="p-2.5 bg-emerald-100 dark:bg-emerald-900/30 rounded-xl mr-4">
            <svg
              class="w-6 h-6 text-emerald-500"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
              />
            </svg>
          </div>
          <div class="text-left">
            <h3 class="text-lg font-semibold text-gray-900 dark:text-white">Sink</h3>
            <p class="text-sm text-gray-500 dark:text-gray-400">Slack destination configuration</p>
          </div>
        </div>
        <div class="flex items-center space-x-3">
          <span
            v-if="form.sink.config.action"
            class="px-2 py-1 text-xs font-medium bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400 rounded-lg capitalize"
          >
            {{ formatSinkAction(form.sink.config.action) }}
          </span>
          <svg
            :class="[
              'w-5 h-5 text-gray-400 transition-transform duration-200',
              sections.sink ? 'rotate-180' : ''
            ]"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M19 9l-7 7-7-7"
            />
          </svg>
        </div>
      </button>

      <!-- Section Content -->
      <transition
        enter-active-class="transition-all duration-300 ease-out"
        enter-from-class="opacity-0 max-h-0"
        enter-to-class="opacity-100 max-h-[2000px]"
        leave-active-class="transition-all duration-200 ease-in"
        leave-from-class="opacity-100 max-h-[2000px]"
        leave-to-class="opacity-0 max-h-0"
      >
        <div
          v-show="sections.sink"
          class="overflow-hidden"
        >
          <div class="p-6 space-y-5">
            <!-- Import Slack URL Button (deprecated - auto-detect on paste is preferred) -->
            <div
              v-if="FEATURE_TOGGLES.ENABLE_SLACK_URL_IMPORT_BUTTON"
              class="flex justify-end"
            >
              <button
                v-if="!showSlackUrlInput"
                type="button"
                class="flex items-center space-x-2 px-4 py-2 text-sm font-medium text-emerald-600 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-900/20 hover:bg-emerald-100 dark:hover:bg-emerald-900/40 border border-emerald-200 dark:border-emerald-700 rounded-xl transition-all duration-200"
                @click="showSlackUrlInput = true"
              >
                <svg
                  class="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1"
                  />
                </svg>
                <span>Import Slack URL</span>
              </button>
            </div>

            <!-- Slack URL Import Input -->
            <div
              v-if="showSlackUrlInput"
              class="p-5 bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-700 rounded-xl space-y-4"
            >
              <div class="flex items-center justify-between">
                <label class="text-sm font-medium text-emerald-700 dark:text-emerald-300">
                  Paste Slack URL
                </label>
                <button
                  type="button"
                  class="p-1.5 text-emerald-500 hover:text-emerald-700 dark:hover:text-emerald-300 hover:bg-emerald-100 dark:hover:bg-emerald-800/30 rounded-lg transition-colors"
                  @click="() => {
                    showSlackUrlInput = false
                    slackUrlInput = ''
                    slackUrlError = null
                  }"
                >
                  <svg
                    class="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>
              <input
                ref="slackUrlInputRef"
                v-model="slackUrlInput"
                type="text"
                class="w-full px-4 py-3 border rounded-xl text-sm font-mono focus:outline-none focus:ring-2 bg-white dark:bg-gray-700 border-emerald-300 dark:border-emerald-600 text-gray-900 dark:text-white focus:ring-emerald-500 focus:border-emerald-500 transition-all"
                placeholder="https://gohighlevel.slack.com/archives/C097UPY34QJ"
                @keydown.enter.prevent="parseSlackUrl"
              />
              <p
                v-if="slackUrlError"
                class="text-xs text-rose-500"
              >
                {{ slackUrlError }}
              </p>
              <p
                v-if="slackUrlSuccess"
                class="text-xs text-emerald-600 dark:text-emerald-400"
              >
                {{ slackUrlSuccess }}
              </p>
              <div class="flex items-center justify-between">
                <p class="text-xs text-emerald-600 dark:text-emerald-400">
                  Supports: Channel, Thread, or DM URLs
                </p>
                <button
                  type="button"
                  class="px-4 py-2 text-sm font-medium text-white bg-emerald-500 hover:bg-emerald-600 rounded-xl transition-colors"
                  @click="parseSlackUrl"
                >
                  Import
                </button>
              </div>
            </div>

            <div class="grid grid-cols-2 gap-5">
              <div>
                <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Action <span class="text-rose-500">*</span>
                </label>
                <select
                  v-model="form.sink.config.action"
                  class="w-full px-4 py-3 border rounded-xl text-sm focus:outline-none focus:ring-2 bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white focus:ring-emerald-500 focus:border-emerald-500 transition-all"
                >
                  <option value="sendToDM">Send to DM</option>
                  <option value="sendToChannel">Send to Channel</option>
                  <option value="sendToThread">Send to Thread</option>
                </select>
              </div>

              <div v-if="form.sink.config.action === 'sendToDM'">
                <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  User ID(s)
                </label>
                <input
                  v-model="userIdsInput"
                  type="text"
                  class="w-full px-4 py-3 border rounded-xl text-sm font-mono focus:outline-none focus:ring-2 bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white focus:ring-emerald-500 focus:border-emerald-500 transition-all"
                  placeholder="U12345678, or paste Slack channel/thread URL"
                  @paste="onSlackFieldPaste($event, 'userId')"
                  @input="clearSlackFieldMessages"
                />
                <p class="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  User ID (starts with U), or paste a Slack channel/thread URL to auto-extract
                </p>
              </div>

              <div
                v-if="
                  form.sink.config.action === 'sendToChannel' ||
                  form.sink.config.action === 'sendToThread'
                "
              >
                <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Channel ID <span class="text-rose-500">*</span>
                </label>
                <input
                  v-model="form.sink.config.channelId"
                  type="text"
                  class="w-full px-4 py-3 border rounded-xl text-sm font-mono focus:outline-none focus:ring-2 bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white focus:ring-emerald-500 focus:border-emerald-500 transition-all"
                  placeholder="C12345678, or paste Slack channel URL"
                  @paste="onSlackFieldPaste($event, 'channelId')"
                  @input="clearSlackFieldMessages"
                />
                <p class="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  Channel ID or paste a Slack URL to auto-extract
                </p>
              </div>
            </div>

            <div v-if="form.sink.config.action === 'sendToThread'">
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Thread Timestamp <span class="text-rose-500">*</span>
              </label>
              <input
                v-model="form.sink.config.threadTs"
                type="text"
                class="w-full px-4 py-3 border rounded-xl text-sm font-mono focus:outline-none focus:ring-2 bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white focus:ring-emerald-500 focus:border-emerald-500 transition-all"
                placeholder="1704700800.123456, or paste Slack thread URL"
                @paste="onSlackFieldPaste($event, 'threadTs')"
                @input="clearSlackFieldMessages"
              />
              <p class="mt-1 text-xs text-gray-500 dark:text-gray-400">
                Thread timestamp or paste a Slack thread URL to auto-extract
              </p>
            </div>

            <!-- Slack URL Parse Feedback -->
            <div
              v-if="slackFieldError"
              class="p-3 bg-rose-50 dark:bg-rose-900/20 border border-rose-200 dark:border-rose-700 rounded-xl"
            >
              <p class="text-sm text-rose-600 dark:text-rose-400">{{ slackFieldError }}</p>
            </div>
            <div
              v-if="slackFieldSuccess"
              class="p-3 bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-700 rounded-xl"
            >
              <p class="text-sm text-emerald-600 dark:text-emerald-400">
                ✓ {{ slackFieldSuccess }}
              </p>
            </div>
          </div>
        </div>
      </transition>
    </div>

    <!-- Error Message -->
    <div
      v-if="submitError"
      class="px-6 py-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-2xl"
    >
      <p class="text-sm text-red-600 dark:text-red-400">{{ submitError }}</p>
    </div>

    <!-- Action Buttons -->
    <div
      :class="[
        theme.isDark.value ? 'bg-gray-800' : 'bg-white',
        'rounded-2xl overflow-hidden shadow-lg-custom px-6 py-5 flex justify-between items-center'
      ]"
      style="box-shadow: var(--shadow-lg)"
    >
      <button
        type="button"
        :disabled="isSubmitting"
        :class="[
          theme.isDark.value
            ? 'bg-gray-600 hover:bg-gray-500 text-gray-200'
            : 'bg-gray-200 hover:bg-gray-300 text-gray-700',
          'px-6 py-2.5 rounded-xl text-sm font-medium transition-all duration-200',
          isSubmitting ? 'opacity-50 cursor-not-allowed' : ''
        ]"
        @click="handleCancel"
      >
        Cancel
      </button>
      <button
        type="submit"
        :disabled="isSubmitting"
        :class="[
          'px-6 py-2.5 bg-emerald-500 hover:bg-emerald-600 text-white rounded-xl text-sm font-medium transition-all duration-200 flex items-center space-x-2',
          isSubmitting ? 'opacity-50 cursor-not-allowed' : ''
        ]"
        @click="handleSubmit"
      >
        <svg
          v-if="isSubmitting"
          class="animate-spin h-4 w-4"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
        >
          <circle
            class="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            stroke-width="4"
          ></circle>
          <path
            class="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
          ></path>
        </svg>
        <span>{{
          isSubmitting ? 'Saving...' : mode === 'edit' ? 'Save Changes' : 'Create Report'
        }}</span>
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, watch, inject, reactive, nextTick } from 'vue'
import { useTheme } from '../../../utils/useTheme'
import type {
  ReportConfig,
  ReportStatus,
  CreateReportRequest,
  UpdateReportRequest,
  ScheduleConfig,
  SourceDefinition,
  SinkDefinition,
  TransformDefinition,
  SlackSinkConfig,
  HttpSourceConfig,
  TemplateTransformConfig,
  ExecuteStageResponse
} from '../../../types/platform-report'
import { platformReportsService } from '../../../services/platform-reports'
import { toString } from 'cronstrue'
import authService from '../../../services/auth/auth'

// ============================================================================
// Feature Toggles - Flip these to enable/disable features
// ============================================================================
const FEATURE_TOGGLES = {
  ENABLE_CURL_IMPORT: true, // Enable "Import cURL" button in HTTP Source section
  ENABLE_CURL_EXPORT: true, // Enable "Export as cURL" button in HTTP Source section
  ENABLE_SLACK_URL_IMPORT_BUTTON: false, // Enable separate "Import Slack URL" button (deprecated)
  ENABLE_SLACK_URL_AUTO_DETECT: true, // Auto-detect Slack URLs when pasted in User ID/Channel ID fields
  ENABLE_SOURCE_PREVIEW: true, // Enable "Preview" button in HTTP Source section (edit mode only)
  ENABLE_TRANSFORM_PREVIEW: true // Enable "Preview" button in Template section (edit mode only)
}

interface ReportFormData {
  name: string
  schedule: ScheduleConfig
  source: SourceDefinition
  transform: TransformDefinition
  sink: SinkDefinition
  status: ReportStatus
}

const props = defineProps<{
  mode: 'create' | 'edit'
  reportData?: ReportConfig | null
}>()

const emit = defineEmits<{
  (e: 'saved'): void
  (e: 'cancel'): void
}>()

const theme = inject('theme', useTheme())

// Collapsible sections state
const sections = reactive({
  source: true,
  template: true,
  sink: true
})

// Cron presets
const cronPresets = [
  { label: 'Daily at 9 AM', value: '0 9 * * *' },
  { label: 'Weekdays at 9 AM', value: '0 9 * * 1-5' },
  { label: 'Every hour', value: '0 * * * *' },
  { label: 'Every 6 hours', value: '0 */6 * * *' },
  { label: 'Weekly (Mon 9 AM)', value: '0 9 * * 1' },
  { label: 'Monthly (1st at 9 AM)', value: '0 9 1 * *' }
]

// File config toggle
const fileConfigEnabled = ref(false)
const fileConfigHeaders = ref('')

// Sort configuration
const sortColumns = ref('')
const sortOrder = ref<'asc' | 'desc'>('desc')

// Headers list for dynamic key-value pairs
const headersList = ref<Array<{ key: string; value: string }>>([])

// Body JSON string
const bodyJson = ref('')
const bodyJsonError = ref<string | null>(null)

// User IDs input (for single or group DM)
const userIdsInput = ref('')

// cURL import state
const showCurlInput = ref(false)
const curlInput = ref('')
const curlError = ref<string | null>(null)
const curlInputRef = ref<HTMLTextAreaElement | null>(null)

// Slack URL import state
const showSlackUrlInput = ref(false)
const slackUrlInput = ref('')
const slackUrlError = ref<string | null>(null)
const slackUrlSuccess = ref<string | null>(null)
const slackUrlInputRef = ref<HTMLInputElement | null>(null)

// Slack field paste error/success messages
const slackFieldError = ref<string | null>(null)
const slackFieldSuccess = ref<string | null>(null)

// Loading state
const isSubmitting = ref(false)
const submitError = ref<string | null>(null)

// Preview state
const sourcePreviewLoading = ref(false)
const sourcePreviewData = ref<ExecuteStageResponse | null>(null)
const sourcePreviewError = ref<string | null>(null)
const showSourcePreview = ref(false)

const transformPreviewLoading = ref(false)
const transformPreviewData = ref<ExecuteStageResponse | null>(null)
const transformPreviewError = ref<string | null>(null)
const showTransformPreview = ref(false)

// State to remember preview open status for post-save restoration
const pendingSourcePreviewRestore = ref(false)
const pendingTransformPreviewRestore = ref(false)

// PDF preview rows expansion
const showAllPdfRows = ref(false)

// Format sink action for display
const formatSinkAction = (action: string) => {
  const actionMap: Record<string, string> = {
    sendToDM: 'DM',
    sendToChannel: 'Channel',
    sendToThread: 'Thread'
  }
  return actionMap[action] || action
}

// Form state
const getDefaultForm = (): ReportFormData => ({
  name: '',
  schedule: {
    type: 'cron',
    expression: '0 9 * * *'
  },
  source: {
    type: 'http',
    config: {
      url: '',
      method: 'GET',
      headers: {},
      timeout: 30000
    } as HttpSourceConfig
  },
  transform: {
    type: 'template',
    config: {
      template: '',
      fileConfig: {
        enabled: false,
        dataPath: 'data.value',
        headers: [],
        topN: 20
      }
    } as TemplateTransformConfig
  },
  sink: {
    type: 'slack',
    config: {
      action: 'sendToDM',
      userId: '',
      userIds: [],
      channelId: '',
      threadTs: '',
      file: {
        filename: '',
        title: '',
        beforeContent: '',
        afterContent: ''
      }
    } as SlackSinkConfig
  },
  status: 'draft'
})

const form = reactive<ReportFormData>(getDefaultForm())

// Reset form to defaults
const resetForm = () => {
  const defaults = getDefaultForm()
  form.name = defaults.name
  form.status = defaults.status
  form.schedule = defaults.schedule
  form.source = defaults.source
  form.transform = defaults.transform
  form.sink = defaults.sink
  fileConfigEnabled.value = false
  fileConfigHeaders.value = ''
  sortColumns.value = ''
  sortOrder.value = 'desc'
  headersList.value = []
  bodyJson.value = ''
  bodyJsonError.value = null
  userIdsInput.value = ''
  submitError.value = null
  // Reset import states
  showCurlInput.value = false
  curlInput.value = ''
  curlError.value = null
  showSlackUrlInput.value = false
  slackUrlInput.value = ''
  slackUrlError.value = null
  slackUrlSuccess.value = null
}

// Watch for edit mode changes
watch(
  () => props.reportData,
  (newData) => {
    if (newData) {
      form.name = newData.name || ''
      form.status = newData.status ?? 'draft'
      form.source = JSON.parse(JSON.stringify(newData.source))
      form.transform = JSON.parse(JSON.stringify(newData.transform))
      form.sink = JSON.parse(JSON.stringify(newData.sink))

      // Handle schedule
      if (newData.schedule) {
        form.schedule = JSON.parse(JSON.stringify(newData.schedule))
      } else {
        form.schedule = { type: 'manual' }
      }

      // Handle headers
      const headers = (newData.source.config as HttpSourceConfig).headers || {}
      headersList.value = Object.entries(headers).map(([key, value]) => ({ key, value }))

      // Handle body
      const body = (newData.source.config as HttpSourceConfig).body
      if (body) {
        bodyJson.value = typeof body === 'string' ? body : JSON.stringify(body, null, 2)
      } else {
        bodyJson.value = ''
      }

      // Handle file config
      const fileConfig = (newData.transform.config as TemplateTransformConfig).fileConfig
      if (fileConfig?.enabled) {
        fileConfigEnabled.value = true
        fileConfigHeaders.value = fileConfig.headers ? fileConfig.headers.join(', ') : ''
        // Handle sort config
        if (fileConfig.sort) {
          sortColumns.value = fileConfig.sort.columns ? fileConfig.sort.columns.join(', ') : ''
          sortOrder.value = fileConfig.sort.order || 'desc'
        } else {
          sortColumns.value = ''
          sortOrder.value = 'desc'
        }
        // Ensure fileConfig exists in form
        if (!form.transform.config.fileConfig) {
          form.transform.config.fileConfig = {
            enabled: true,
            dataPath: fileConfig.dataPath,
            headers: fileConfig.headers,
            topN: fileConfig.topN,
            sort: fileConfig.sort
          }
        }
      } else {
        fileConfigEnabled.value = false
        fileConfigHeaders.value = ''
        sortColumns.value = ''
        sortOrder.value = 'desc'
      }

      // Handle user IDs
      const sinkConfig = newData.sink.config as SlackSinkConfig
      if (sinkConfig.userIds && sinkConfig.userIds.length > 0) {
        userIdsInput.value = sinkConfig.userIds.join(', ')
      } else if (sinkConfig.userId) {
        userIdsInput.value = sinkConfig.userId
      } else {
        userIdsInput.value = ''
      }

      // Ensure file config exists in sink
      if (!form.sink.config.file) {
        form.sink.config.file = {
          filename: '',
          title: '',
          beforeContent: '',
          afterContent: ''
        }
      }
      if (sinkConfig.file) {
        form.sink.config.file = { ...sinkConfig.file }
      }

      // Restore preview states if they were open before save
      nextTick(() => {
        if (pendingSourcePreviewRestore.value) {
          pendingSourcePreviewRestore.value = false
          previewSourceStage()
        }
        if (pendingTransformPreviewRestore.value) {
          pendingTransformPreviewRestore.value = false
          previewTransformStage()
        }
      })
    } else if (props.mode === 'create') {
      resetForm()
    }
  },
  { immediate: true }
)

// Watch fileConfigEnabled to initialize fileConfig
watch(fileConfigEnabled, (enabled) => {
  if (enabled) {
    if (!form.transform.config.fileConfig) {
      form.transform.config.fileConfig = {
        enabled: true,
        dataPath: 'data.value',
        headers: [],
        topN: 20
      }
    } else {
      form.transform.config.fileConfig.enabled = true
    }
    // Initialize file in sink if not exists
    if (!form.sink.config.file) {
      form.sink.config.file = {
        filename: '',
        title: '',
        beforeContent: '',
        afterContent: ''
      }
    }
  } else {
    if (form.transform.config.fileConfig) {
      form.transform.config.fileConfig.enabled = false
    }
  }
})

const addHeader = () => {
  headersList.value.push({ key: '', value: '' })
}

const removeHeader = (index: number) => {
  headersList.value.splice(index, 1)
}

const handleCancel = () => {
  emit('cancel')
}

// Parse cURL command and populate form fields
const parseCurlCommand = () => {
  curlError.value = null
  const curl = curlInput.value.trim()

  if (!curl) {
    curlError.value = 'Please paste a cURL command'
    return
  }

  try {
    // Extract URL - handle both single and double quotes
    const urlMatch =
      curl.match(/curl\s+(?:-X\s+\w+\s+)?['"]?(https?:\/\/[^\s'"]+)['"]?/i) ||
      curl.match(/['"]?(https?:\/\/[^\s'"]+)['"]?/)

    if (!urlMatch) {
      curlError.value = 'Could not find a valid URL in the cURL command'
      return
    }

    const url = urlMatch[1]
    form.source.config.url = url

    // Extract method (default to GET)
    const methodMatch = curl.match(/-X\s+(\w+)/i)
    const method = methodMatch ? methodMatch[1].toUpperCase() : 'GET'
    if (['GET', 'POST', 'PUT', 'PATCH', 'DELETE'].includes(method)) {
      form.source.config.method = method as 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE'
    }

    // If there's -d or --data, it's likely a POST
    if (!methodMatch && (curl.includes('-d ') || curl.includes('--data '))) {
      form.source.config.method = 'POST'
    }

    // Extract headers
    const headerRegex = /-H\s+['"]([^'"]+)['"]/gi
    const newHeaders: Array<{ key: string; value: string }> = []
    let headerMatch

    while ((headerMatch = headerRegex.exec(curl)) !== null) {
      const headerStr = headerMatch[1]
      const colonIndex = headerStr.indexOf(':')
      if (colonIndex > 0) {
        const key = headerStr.substring(0, colonIndex).trim()
        const value = headerStr.substring(colonIndex + 1).trim()
        newHeaders.push({ key, value })
      }
    }

    if (newHeaders.length > 0) {
      headersList.value = newHeaders
    }

    // Extract body data
    const dataMatch =
      curl.match(/(?:-d|--data|--data-raw)\s+['"](.+?)['"]\s*(?:-|$)/s) ||
      curl.match(/(?:-d|--data|--data-raw)\s+'([^']+)'/s) ||
      curl.match(/(?:-d|--data|--data-raw)\s+"([^"]+)"/s)

    if (dataMatch) {
      const bodyData = dataMatch[1]
      try {
        // Try to parse and format as JSON
        const parsed = JSON.parse(bodyData)
        bodyJson.value = JSON.stringify(parsed, null, 2)
      } catch {
        // If not valid JSON, use as-is
        bodyJson.value = bodyData
      }
    }

    // Close the input and show success
    showCurlInput.value = false
    curlInput.value = ''
  } catch {
    curlError.value = 'Failed to parse cURL command. Please check the format.'
  }
}

// Export current HTTP source config as cURL command
const exportAsCurl = () => {
  const config = form.source.config
  const parts: string[] = ['curl']

  // Add method (skip for GET as it's default)
  if (config.method !== 'GET') {
    parts.push(`-X ${config.method}`)
  }

  // Add URL
  parts.push(`'${config.url}'`)

  // Add headers from headersList
  for (const header of headersList.value) {
    if (header.key && header.value) {
      parts.push(`-H '${header.key}: ${header.value}'`)
    }
  }

  // Add body for POST/PUT/PATCH
  if (['POST', 'PUT', 'PATCH'].includes(config.method) && bodyJson.value.trim()) {
    // Escape single quotes in the body
    const escapedBody = bodyJson.value.replace(/'/g, "'\\''")
    parts.push(`-d '${escapedBody}'`)
  }

  const curlCommand = parts.join(' \\\n  ')

  // Copy to clipboard
  navigator.clipboard
    .writeText(curlCommand)
    .then(() => {
      // Show a brief success message (could use a toast here)
      alert('cURL command copied to clipboard!')
    })
    .catch(() => {
      // Fallback: show the command in a prompt
      prompt('Copy this cURL command:', curlCommand)
    })
}

// Parse Slack URL and populate sink config
const parseSlackUrl = () => {
  slackUrlError.value = null
  slackUrlSuccess.value = null
  const url = slackUrlInput.value.trim()

  if (!url) {
    slackUrlError.value = 'Please paste a Slack URL'
    return
  }

  try {
    // Slack URL patterns:
    // User Profile: https://gohighlevel.enterprise.slack.com/team/U07AHULLYEM
    // Channel: https://gohighlevel.enterprise.slack.com/archives/C097UPY34QJ
    // Thread: https://gohighlevel.slack.com/archives/C07B7JRBE57/p1767606812836559
    // DM Conversation (invalid): https://gohighlevel.enterprise.slack.com/archives/D07AHULNTBK

    // Check for user profile URL first: /team/U...
    const teamMatch = url.match(/\/team\/([A-Z0-9]+)/i)
    if (teamMatch) {
      const userId = teamMatch[1]
      const firstChar = userId.charAt(0).toUpperCase()

      if (firstChar === 'U') {
        // Valid user ID from profile URL
        form.sink.config.action = 'sendToDM'
        userIdsInput.value = userId
        form.sink.config.channelId = ''
        form.sink.config.threadTs = ''
        slackUrlSuccess.value = `User ID detected: ${userId}`

        // Close the input after a short delay
        setTimeout(() => {
          showSlackUrlInput.value = false
          slackUrlInput.value = ''
          slackUrlSuccess.value = null
        }, 1500)
        return
      } else {
        slackUrlError.value = `Invalid team URL: "${userId}" doesn't look like a User ID (should start with U).`
        return
      }
    }

    // Extract the archive ID (channel/DM ID)
    const archiveMatch = url.match(/\/archives\/([A-Z0-9]+)/i)

    if (!archiveMatch) {
      slackUrlError.value =
        'Could not find a valid Slack channel/user ID in the URL. Supported: /team/U..., /archives/C..., /archives/.../p...'
      return
    }

    const id = archiveMatch[1]

    // Check for thread timestamp (format: /pXXXXXXXXXXXXXXXX)
    const threadMatch = url.match(/\/p(\d+)$/)

    // Determine type based on ID prefix:
    // C = Channel, G = Group/Private Channel, U = User (from /team/ URL)
    const firstChar = id.charAt(0).toUpperCase()

    if (threadMatch) {
      // It's a thread - extract timestamp
      // Slack thread ts format: first 10 digits are seconds, rest are microseconds
      // URL format p1767606812836559 -> 1767606812.836559
      const rawTs = threadMatch[1]
      const threadTs =
        rawTs.length > 10 ? `${rawTs.substring(0, 10)}.${rawTs.substring(10)}` : rawTs

      form.sink.config.action = 'sendToThread'
      form.sink.config.channelId = id
      form.sink.config.threadTs = threadTs
      slackUrlSuccess.value = `Thread detected: Channel ${id}, Thread ${threadTs}`
    } else if (firstChar === 'C' || firstChar === 'G') {
      // It's a channel (C) or private channel/group (G)
      form.sink.config.action = 'sendToChannel'
      form.sink.config.channelId = id
      form.sink.config.threadTs = ''
      slackUrlSuccess.value = `Channel detected: ${id}`
    } else if (firstChar === 'D') {
      // D = DM conversation ID - this is NOT a user ID
      slackUrlError.value = `Invalid URL: "${id}" is a DM conversation ID, not a User ID. For DMs, paste the user's profile URL (slack.com/team/U...) or enter the User ID directly.`
      return
    } else {
      // Unknown type
      slackUrlError.value = `Unknown Slack ID type: ${id}. Expected Channel (C...), Thread, or User profile URL (/team/U...).`
      return
    }

    // Close the input after a short delay to show success message
    setTimeout(() => {
      showSlackUrlInput.value = false
      slackUrlInput.value = ''
      slackUrlSuccess.value = null
    }, 1500)
  } catch {
    slackUrlError.value = 'Failed to parse Slack URL. Please check the format.'
  }
}

// Clear Slack field messages when user types
const clearSlackFieldMessages = () => {
  slackFieldError.value = null
  slackFieldSuccess.value = null
}

// Auto-detect and parse Slack URL on paste in User ID/Channel ID fields
const onSlackFieldPaste = (
  event: ClipboardEvent,
  fieldType: 'userId' | 'channelId' | 'threadTs'
) => {
  // Clear previous messages
  slackFieldError.value = null
  slackFieldSuccess.value = null

  if (!FEATURE_TOGGLES.ENABLE_SLACK_URL_AUTO_DETECT) return

  const pastedText = event.clipboardData?.getData('text')?.trim()
  if (!pastedText) return

  // Check if it looks like a Slack URL (supports /archives/ and /team/ URLs)
  const isSlackUrl =
    pastedText.includes('slack.com/archives/') || pastedText.includes('slack.com/team/')
  if (!isSlackUrl) return

  // Prevent default paste behavior - we'll handle it
  event.preventDefault()

  try {
    // Check for user profile URL first: /team/U...
    const teamMatch = pastedText.match(/\/team\/([A-Z0-9]+)/i)
    if (teamMatch) {
      const userId = teamMatch[1]
      const firstChar = userId.charAt(0).toUpperCase()

      if (firstChar === 'U') {
        // Valid user ID from profile URL
        form.sink.config.action = 'sendToDM'
        userIdsInput.value = userId
        form.sink.config.channelId = ''
        form.sink.config.threadTs = ''
        slackFieldSuccess.value = `User ID detected: ${userId}`
        return
      } else {
        slackFieldError.value = `Invalid team URL: "${userId}" doesn't look like a User ID (should start with U).`
        return
      }
    }

    // Extract the archive ID (channel/DM ID)
    const archiveMatch = pastedText.match(/\/archives\/([A-Z0-9]+)/i)
    if (!archiveMatch) {
      // Not a valid Slack URL, let the normal paste happen
      return
    }

    const id = archiveMatch[1]

    // Check for thread timestamp (format: /pXXXXXXXXXXXXXXXX)
    const threadMatch = pastedText.match(/\/p(\d+)$/)

    // Determine type based on ID prefix
    const firstChar = id.charAt(0).toUpperCase()

    if (threadMatch) {
      // It's a thread URL - extract channel and timestamp
      const rawTs = threadMatch[1]
      const threadTs =
        rawTs.length > 10 ? `${rawTs.substring(0, 10)}.${rawTs.substring(10)}` : rawTs

      form.sink.config.action = 'sendToThread'
      form.sink.config.channelId = id
      form.sink.config.threadTs = threadTs
      slackFieldSuccess.value = `Thread detected: Channel ${id}, Thread ${threadTs}`
    } else if (firstChar === 'C' || firstChar === 'G') {
      // It's a channel (C) or private channel/group (G)
      form.sink.config.action = 'sendToChannel'
      form.sink.config.channelId = id
      form.sink.config.threadTs = ''
      slackFieldSuccess.value = `Channel detected: ${id}`
    } else if (firstChar === 'D') {
      // D = DM conversation ID - this is NOT a user ID
      slackFieldError.value = `Invalid URL: "${id}" is a DM conversation ID, not a User ID. For DMs, paste the user's profile URL (slack.com/team/U...) or enter the User ID directly.`
      return
    } else {
      // Unknown type - show error
      slackFieldError.value = `Unknown Slack ID type: ${id}. Expected Channel (C...), Thread, or User ID (U...).`
      return
    }
  } catch {
    // If parsing fails, just paste the original text
    if (fieldType === 'userId') {
      userIdsInput.value = pastedText
    } else if (fieldType === 'channelId') {
      form.sink.config.channelId = pastedText
    } else if (fieldType === 'threadTs') {
      form.sink.config.threadTs = pastedText
    }
  }
}

// Helper to safely get fileData from preview response
const getFileData = (data: Record<string, unknown> | undefined): unknown => {
  if (!data) return null
  return data.fileData || null
}

// Helper to get file data row count
const getFileDataRowCount = (data: Record<string, unknown> | undefined): number => {
  const fileData = getFileData(data) as { rows?: unknown[] } | null
  return fileData?.rows?.length || 0
}

// Helper to get file data headers
const getFileDataHeaders = (data: Record<string, unknown> | undefined): string[] => {
  const fileData = getFileData(data) as { headers?: string[] } | null
  return fileData?.headers || []
}

// Helper to get file data rows (limited)
const getFileDataRows = (
  data: Record<string, unknown> | undefined,
  limit: number
): Record<string, unknown>[] => {
  const fileData = getFileData(data) as { rows?: Record<string, unknown>[] } | null
  const rows = fileData?.rows || []
  return rows.slice(0, limit)
}

// Helper to get sink file config for PDF preview
const getSinkFileConfig = () => {
  return form.sink.config.file || { title: '', beforeContent: '', afterContent: '', filename: '' }
}

// Format Slack message markdown to HTML for preview
const formatSlackMessage = (message: string): string => {
  if (!message) return ''

  const html = message
    // Escape HTML first
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    // Bold: *text* -> <strong>text</strong>
    .replace(/\*([^*\n]+)\*/g, '<strong>$1</strong>')
    // Italic: _text_ -> <em>text</em>
    .replace(/_([^_\n]+)_/g, '<em class="text-gray-400">$1</em>')
    // Strikethrough: ~text~ -> <del>text</del>
    .replace(/~([^~\n]+)~/g, '<del>$1</del>')
    // Code: `text` -> <code>text</code>
    .replace(
      /`([^`\n]+)`/g,
      '<code class="px-1 py-0.5 bg-gray-700 rounded text-pink-400 text-sm">$1</code>'
    )
    // Links: <url|text> -> <a>text</a>
    .replace(
      /&lt;([^|>]+)\|([^>]+)&gt;/g,
      '<a href="$1" class="text-blue-400 hover:underline" target="_blank">$2</a>'
    )
    // Plain links: <url> -> <a>url</a>
    .replace(
      /&lt;(https?:\/\/[^>]+)&gt;/g,
      '<a href="$1" class="text-blue-400 hover:underline" target="_blank">$1</a>'
    )
    // Newlines to <br>
    .replace(/\n/g, '<br>')

  return html
}

// Parse cron expression to human-readable format using cronstrue
const parseCronExpression = (expression: string): string => {
  if (!expression || expression.trim() === '') {
    return 'Enter a cron expression to see the schedule'
  }

  try {
    return toString(expression, {
      use24HourTimeFormat: false,
      throwExceptionOnParseError: false
    })
  } catch {
    return 'Invalid cron expression format'
  }
}

// Get IST equivalent time for cron expression
const getISTTime = (expression: string): string | null => {
  if (!expression || expression.trim() === '') {
    return null
  }

  try {
    const parts = expression.trim().split(/\s+/)
    if (parts.length !== 5) {
      return null
    }

    const [minute, hour] = parts

    // Only show IST conversion if we have specific hour and minute (not wildcards)
    if (hour === '*' || minute === '*') {
      return null
    }

    const utcHour = parseInt(hour, 10)
    const utcMinute = parseInt(minute, 10)

    if (isNaN(utcHour) || isNaN(utcMinute)) {
      return null
    }

    // IST is UTC + 5:30
    let istHour = utcHour + 5
    let istMinute = utcMinute + 30

    if (istMinute >= 60) {
      istMinute -= 60
      istHour += 1
    }

    if (istHour >= 24) {
      istHour -= 24
    }

    const period = istHour >= 12 ? 'PM' : 'AM'
    const displayHour = istHour > 12 ? istHour - 12 : istHour === 0 ? 12 : istHour
    const displayMinute = istMinute.toString().padStart(2, '0')

    return `At ${displayHour}:${displayMinute} ${period} IST daily`
  } catch {
    return null
  }
}

// Format epoch timestamp (milliseconds) to IST
const formatEpochToIST = (epochMs?: number | string) => {
  if (!epochMs) return '-'
  // Handle both number and string (for backward compatibility)
  const timestamp = typeof epochMs === 'string' ? parseInt(epochMs, 10) : epochMs
  if (isNaN(timestamp)) return '-'
  const date = new Date(timestamp)
  return `${date.toLocaleString('en-IN', {
    timeZone: 'Asia/Kolkata',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: true
  })} IST`
}

// Preview functions
const previewSourceStage = async () => {
  if (!props.reportData?.id) {
    sourcePreviewError.value = 'Please save the report first to preview'
    return
  }

  sourcePreviewLoading.value = true
  sourcePreviewError.value = null
  sourcePreviewData.value = null

  try {
    const response = await platformReportsService.executeReportStage(props.reportData.id, 'source')

    // Ensure the response has the expected structure
    // Handle both direct response and wrapped response scenarios
    const normalizedResponse: ExecuteStageResponse = {
      code: response.code ?? 200,
      success: response.success ?? response.code === 200,
      stage: response.stage ?? 'source',
      data: response.data ?? response,
      message: response.message ?? '',
      traceId: response.traceId ?? ''
    }

    sourcePreviewData.value = normalizedResponse
    showSourcePreview.value = true
  } catch (error) {
    sourcePreviewError.value = error instanceof Error ? error.message : 'Failed to preview source'
  } finally {
    sourcePreviewLoading.value = false
  }
}

const previewTransformStage = async () => {
  if (!props.reportData?.id) {
    transformPreviewError.value = 'Please save the report first to preview'
    return
  }

  transformPreviewLoading.value = true
  transformPreviewError.value = null
  transformPreviewData.value = null
  showAllPdfRows.value = false // Reset row expansion

  try {
    const response = await platformReportsService.executeReportStage(
      props.reportData.id,
      'transform'
    )

    // Ensure the response has the expected structure
    // Handle both direct response and wrapped response scenarios
    const normalizedResponse: ExecuteStageResponse = {
      code: response.code ?? 200,
      success: response.success ?? response.code === 200,
      stage: response.stage ?? 'transform',
      data: response.data ?? response,
      message: response.message ?? '',
      traceId: response.traceId ?? ''
    }

    transformPreviewData.value = normalizedResponse
    showTransformPreview.value = true
  } catch (error) {
    transformPreviewError.value =
      error instanceof Error ? error.message : 'Failed to preview transform'
  } finally {
    transformPreviewLoading.value = false
  }
}

const handleSubmit = async () => {
  isSubmitting.value = true
  submitError.value = null
  bodyJsonError.value = null

  try {
    // Build headers from list
    const headers: Record<string, string> = {}
    headersList.value.forEach((h) => {
      if (h.key.trim()) {
        headers[h.key.trim()] = h.value
      }
    })

    // Parse body JSON if present
    let body: Record<string, unknown> | undefined
    if (bodyJson.value.trim() && ['POST', 'PUT', 'PATCH'].includes(form.source.config.method)) {
      try {
        body = JSON.parse(bodyJson.value)
      } catch {
        bodyJsonError.value = 'Invalid JSON format'
        isSubmitting.value = false
        return
      }
    }

    // Build file config headers
    const fileHeaders = fileConfigHeaders.value
      .split(',')
      .map((h) => h.trim())
      .filter((h) => h.length > 0)

    // Parse user IDs
    const userIdsParsed = userIdsInput.value
      .split(',')
      .map((id) => id.trim())
      .filter((id) => id.length > 0)

    // Build schedule config
    const scheduleConfig: ScheduleConfig = {
      type: form.schedule.type
    }
    if (form.schedule.type === 'cron' && form.schedule.expression) {
      scheduleConfig.expression = form.schedule.expression
    }

    // Build source config
    const sourceConfig: HttpSourceConfig = {
      url: form.source.config.url,
      method: form.source.config.method,
      timeout: form.source.config.timeout || 30000
    }
    if (Object.keys(headers).length > 0) {
      sourceConfig.headers = headers
    }
    if (body) {
      sourceConfig.body = body
    }

    // Build transform config
    const transformConfig: TemplateTransformConfig = {
      template: form.transform.config.template
    }
    if (fileConfigEnabled.value && form.transform.config.fileConfig) {
      // Parse sort columns
      const sortColumnsParsed = sortColumns.value
        .split(',')
        .map((c) => c.trim())
        .filter((c) => c.length > 0)

      transformConfig.fileConfig = {
        enabled: true,
        dataPath: form.transform.config.fileConfig.dataPath,
        headers: fileHeaders.length > 0 ? fileHeaders : undefined,
        topN: form.transform.config.fileConfig.topN
      }

      // Add sort config only if columns are specified
      if (sortColumnsParsed.length > 0) {
        transformConfig.fileConfig.sort = {
          columns: sortColumnsParsed,
          order: sortOrder.value
        }
      }
    }

    // Build sink config
    const sinkConfig: SlackSinkConfig = {
      action: form.sink.config.action
    }

    if (form.sink.config.action === 'sendToDM') {
      if (userIdsParsed.length === 1) {
        sinkConfig.userId = userIdsParsed[0]
      } else if (userIdsParsed.length > 1) {
        sinkConfig.userIds = userIdsParsed
      }
    } else {
      sinkConfig.channelId = form.sink.config.channelId
    }

    if (form.sink.config.action === 'sendToThread') {
      sinkConfig.threadTs = form.sink.config.threadTs
    }

    // Add file config if enabled
    if (fileConfigEnabled.value && form.sink.config.file?.filename) {
      sinkConfig.file = {
        filename: form.sink.config.file.filename,
        title: form.sink.config.file.title,
        beforeContent: form.sink.config.file.beforeContent,
        afterContent: form.sink.config.file.afterContent
      }
    }

    if (props.mode === 'edit' && props.reportData) {
      // Update existing report
      // Get current user email
      const user = authService.getUser()
      const userEmail = user?.email || ''

      const updateData: UpdateReportRequest = {
        name: form.name || undefined,
        schedule: scheduleConfig,
        status: form.status,
        source: {
          type: 'http',
          config: sourceConfig
        },
        transform: {
          type: 'template',
          config: transformConfig
        },
        sink: {
          type: 'slack',
          config: sinkConfig
        },
        meta: {
          updatedBy: userEmail
        }
      }

      // Remember preview states before save triggers data refresh
      pendingSourcePreviewRestore.value = showSourcePreview.value
      pendingTransformPreviewRestore.value = showTransformPreview.value

      await platformReportsService.updateReport(props.reportData.id, updateData)
      emit('saved')
    } else {
      // Create new report
      // Get current user email
      const user = authService.getUser()
      const userEmail = user?.email || ''

      const createData: CreateReportRequest = {
        name: form.name || undefined,
        schedule: scheduleConfig,
        status: form.status,
        source: {
          type: 'http',
          config: sourceConfig
        },
        transform: {
          type: 'template',
          config: transformConfig
        },
        sink: {
          type: 'slack',
          config: sinkConfig
        },
        meta: {
          createdBy: userEmail
        }
      }

      await platformReportsService.createReport(createData)
      emit('saved')
    }
  } catch (err) {
    submitError.value =
      err instanceof Error ? err.message : 'An error occurred while saving the report'
  } finally {
    isSubmitting.value = false
  }
}
</script>
